import { type Option } from "#/types/options";
import { type TransformedOrderbook } from "#/types/orderbook";
import { type TradeDirection } from "#/types/trade";
import { calculateEstimatedExecutionPrice } from "#/utils/estimatedExecPrice.utils";
import { toDirection } from "#/utils/marketActionConverter";

export interface HedgeDetails {
  deltaToHedge: number;
  hedgeDirection: TradeDirection;
  hedgeIsBuy: boolean;
  hedgeAmount: number;
  hedgeLimitPrice: number;
  hedgeEstimatedExecPrice: number;
}

export interface CalculateHedgeOptions {
  option?: Option;
  optionSize: string;
  indexPrice: number;
  slippageFactor: number;
  perpsOrderbook: TransformedOrderbook;
}

export const calculateHedge = ({
  option,
  optionSize,
  indexPrice,
  slippageFactor,
  perpsOrderbook,
}: CalculateHedgeOptions): HedgeDetails | undefined => {
  if (!option) return;

  const { delta, isBuy } = option;

  if (!delta) return;

  const netDelta = Number(optionSize) * delta;
  const deltaToHedge = netDelta * (isBuy ? 1 : -1);
  const hedgeIsBuy = deltaToHedge < 0;
  const hedgeDirection = toDirection(hedgeIsBuy);
  const hedgeAmount = Math.abs(deltaToHedge);
  const slippageMultiplier = hedgeIsBuy
    ? 1 + slippageFactor
    : 1 - slippageFactor;
  const hedgeLimitPrice = indexPrice * slippageMultiplier;
  const { executionPrice } = calculateEstimatedExecutionPrice(hedgeAmount, {
    bids: perpsOrderbook.buys,
    asks: perpsOrderbook.sells,
    isBuy: hedgeIsBuy,
  });

  return {
    deltaToHedge,
    hedgeIsBuy,
    hedgeAmount,
    hedgeDirection,
    hedgeLimitPrice,
    hedgeEstimatedExecPrice: executionPrice,
  };
};
